import { Activity, CalendarClock, Euro, HouseIcon, MenuIcon, TrendingUp } from 'lucide-react';
import { InspectionIcon } from '@/components/entity-icons';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchRoutes, useLocation, useNavigate, useParams } from 'react-router';

import { ROLE } from '@/common/role';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePermission } from '@/hooks/use-permission';
import { cn } from '@/lib/utils';

export function MobileAssetNav() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { assetId } = useParams<{ assetId: string }>();
	const isNew = assetId === 'new';
	const parentPath = `/assets/${assetId}`;
	const hasWorkPackageReadPermission = usePermission(ROLE.WORK_PACKAGES_READ);
	const hasSensorsReadPermission = usePermission(ROLE.SENSORS_READ);
	const hasInspectionManagementPagePermission = usePermission(ROLE.INSPECTION_MANAGEMENT_PAGE_VIEW);

	const { isHealthRoute, isTimePlanningRoute, isFinanceRoute, isLifespanRoute } = useMemo(
		() => ({
			isFinanceRoute: !!(matchRoutes([{ path: '/assets/:assetId/finance/*' }], location)?.length),
			isTimePlanningRoute: !!(
				matchRoutes([{ path: '/assets/:assetId/time-planning/*' }], location)?.length
			),
			isHealthRoute: !!(matchRoutes([{ path: '/assets/:assetId/health/*' }], location)?.length),
			isLifespanRoute: !!(matchRoutes([{ path: '/assets/:assetId/lifespan/*' }], location)?.length),
		}),
		[location],
	);

	const isAssetInspectionRoute =
		!isHealthRoute && !isTimePlanningRoute && !isFinanceRoute && !isLifespanRoute;

	const tools = [
		{
			id: 'home',
			icon: HouseIcon,
			label: t('assets.nav.home'),
			// Back to the list with this asset still selected there
			onClick: () => navigate(isNew ? '/' : `/?selected=${assetId}`),
			isActive: false,
			disabled: false,
		},
		{
			id: 'inspections',
			icon: InspectionIcon,
			label: t('assets.nav.inspections'),
			onClick: () => navigate(parentPath),
			isActive: isAssetInspectionRoute,
			disabled: false,
			hidden: !hasInspectionManagementPagePermission,
		},
		{
			id: 'finance',
			icon: Euro,
			label: t('assets.nav.finance'),
			onClick: () => navigate(`${parentPath}/finance`),
			disabled: isNew,
			hidden: !hasWorkPackageReadPermission,
			isActive: isFinanceRoute,
		},
		{
			id: 'time-planning',
			icon: CalendarClock,
			label: t('assets.nav.timePlanning'),
			onClick: () => navigate(`${parentPath}/time-planning`),
			disabled: isNew,
			isActive: isTimePlanningRoute,
		},
		{
			id: 'health',
			icon: Activity,
			label: t('assets.nav.health'),
			onClick: () => navigate(`${parentPath}/health`),
			disabled: isNew,
			hidden: !hasSensorsReadPermission,
			isActive: isHealthRoute,
		},
		{
			id: 'lifespan',
			icon: TrendingUp,
			label: t('assets.nav.lifespan'),
			onClick: () => navigate(`${parentPath}/lifespan`),
			disabled: isNew,
			isActive: isLifespanRoute,
		},
	].filter((tool) => !('hidden' in tool && tool.hidden));

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative h-9 w-9 rounded-full bg-muted/30 border border-border p-0 shadow-sm hover:bg-muted/50 transition-colors"
				>
					<MenuIcon className="size-[18px] text-muted-foreground" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-52">
				{tools.map((tool, index) => (
					<Fragment key={tool.id}>
						{index === 1 && <DropdownMenuSeparator />}
						<DropdownMenuItem
							className={cn(
								'cursor-pointer gap-2',
								tool.isActive &&
									'bg-primary text-primary-foreground focus:bg-primary/90 focus:text-primary-foreground',
							)}
							onClick={tool.onClick}
							disabled={tool.disabled}
						>
							<tool.icon className="size-4" />
							<span>{tool.label}</span>
						</DropdownMenuItem>
					</Fragment>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
